import CardCommon from '@op/components/CardCommon';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSummaryListColumns } from '@op/components/TableCommonHook/hooks/useSummaryListColumns';
import TableCommonHook from '@op/components/TableCommonHook';
import ReportAccessPanel, {
  ReportOwnerSegment,
  type TReportOwner,
} from '@op/components/ReportPermission';
import { useOpenReportPermission } from '@op/hooks/useOpenReportPermission';
import { useAiReportSummaryList } from '../../hooks';
import { Justify, Segment, TabPanel, Tabs } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import {
  useAiReportNewTag,
  useDownloadSummaryItem,
  usePreviewSummaryItem,
} from '@op/pages/AIAnalysisHub/hooks';
import { useStateRef } from '@op/hooks/common';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import SummaryModal, { useModalData } from '../../component/SummaryModal';

interface ISummaryList {
  gameInfo: any;
  wholeGameInfo: any;
  needRequest: boolean;
  userInfo: any;
  tab: 'oneTime' | 'subscriptions';
  onActive: (tabInfo: { id: 'oneTime' | 'subscriptions' }) => void;
  owner: TReportOwner;
  onOwnerActive: (tabInfo: { id: TReportOwner }) => void;
}

const keysMap: any = {
  oneTime: ['analysis_name', 'create_time', 'creator', 'status', 'permission', 'action'],
  subscriptions: [
    'analysis_name',
    'create_time',
    'last_report_generated_time',
    'creator',
    'status',
    'permission',
    'multi_action',
  ],
};

export default function SummaryList({
  gameInfo,
  wholeGameInfo,
  userInfo,
  needRequest,
  tab,
  onActive,
  owner,
  onOwnerActive,
}: ISummaryList) {
  const { formatMessage } = useLanguage();
  const { modalProps, modalVisible, modalType, toggleModal, requestSummary } =
    useModalData();

  const [totalMapRef, totalMap, setTotalMap] = useStateRef({
    oneTime: 0,
    subscriptions: 0,
  });

  const segmentOptions = useMemo(() => {
    return [
      {
        id: 'oneTime',
        text: (
          <div className="dbo-inlineflex">
            <span>
              {formatMessage(
                { id: 'One-time Summary({total})' },
                { total: totalMap?.oneTime },
              )}
            </span>
          </div>
        ),
        label: 'One-time Summary',
        value: 'oneTime',
      },
      {
        id: 'subscriptions',
        text: (
          <div className="dbo-inlineflex">
            <span>
              {formatMessage(
                { id: 'Subscriptions({totalSubscriptions})' },
                { totalSubscriptions: totalMap?.subscriptions },
              )}
            </span>
          </div>
        ),
        label: 'Subscriptions Summary',
        value: 'subscriptions',
      },
    ];
  }, [formatMessage, totalMap]);

  return (
    <>
      <CardCommon className="dbo-custom-ai-summary-list">
        <Justify
          left={
            <div className="dbo-custom-ai-summary-list-segments">
              <ReportOwnerSegment
                value={owner}
                onChange={(value: TReportOwner) => {
                  if (value === owner) return;
                  buriedBtnPoint('ReportOwner', value);
                  onOwnerActive({ id: value });
                  onActive({ id: 'oneTime' });
                }}
                showAll
              />
              <Segment
                className="segment-font12"
                value={tab}
                onChange={(value: 'oneTime' | 'subscriptions') => {
                  buriedBtnPoint('SummaryType', value);
                  onActive({ id: value });
                }}
                options={segmentOptions}
              />
            </div>
          }
        />
        <CardCommon className="dbo-custom-ai-summary-list-table" contentCard>
          <Tabs
            className="dbo-hide-tabs"
            tabs={segmentOptions}
            activeId={tab}
            destroyInactiveTabPanel={false}
          >
            {segmentOptions.map((item) => {
              const { value } = item;
              const keys = keysMap[value];

              return (
                <TabPanel forceRender key={value} id={value}>
                  <SummaryListTable
                    gameInfo={gameInfo}
                    owner={owner}
                    isSelected={tab === value}
                    isOneTime={value === 'oneTime'}
                    wholeGameInfo={wholeGameInfo}
                    keys={keys}
                    toggleModal={toggleModal}
                    setTotal={(total) => {
                      setTotalMap({ ...totalMapRef.current, [value]: total });
                    }}
                    needRequest={needRequest}
                    changeRequestSummary={(value) =>
                      (requestSummary.current = value)
                    }
                  ></SummaryListTable>
                </TabPanel>
              );
            })}
          </Tabs>
        </CardCommon>
      </CardCommon>
      {modalVisible && (
        <SummaryModal
          visible={modalVisible}
          modalType={modalType}
          toggleVisible={toggleModal}
          userInfo={userInfo}
          formatMessage={formatMessage}
          gameInfo={gameInfo}
          wholeGameInfo={wholeGameInfo}
          category={"repeat_summary_task"}
          request={() => requestSummary.current?.()}
          {...modalProps}
        />
      )}
    </>
  );
}

function SummaryListTable({
  gameInfo,
  owner,
  isOneTime,
  keys,
  toggleModal,
  setTotal,
  changeRequestSummary,
  isSelected,
  needRequest,
}: {
  gameInfo: any;
  owner: TReportOwner;
  isOneTime: boolean;
  wholeGameInfo: any;
  keys: Array<string>;
  toggleModal: (data: any) => void;
  setTotal: (value: number) => void;
  changeRequestSummary: (value: any) => void;
  isSelected: boolean;
  needRequest: boolean;
}) {
  const {
    loading,
    deleteSummary,
    sortBy,
    setSortBy,
    pageInfo,
    changePageInfo,
    records,
    total,
    requestSummaryList,
    params,
    stopSummary
  } = useAiReportSummaryList({
    gameInfo,
    isOneTime,
    needRequest,
    owner,
  });

  useEffect(() => {
    setTotal(total);
  }, [total]);

  useEffect(() => {
    if (isSelected) {
      changeRequestSummary(() => requestSummaryList(params));
    }
  }, [isSelected, params]);

  const clickDelete = async (records: { id: string; name: string }) => {
    buriedBtnPoint(
      'Delete',
      isOneTime ? 'One-time Summary' : 'Subscriptions Summary',
    );
    const params = { ...gameInfo, ...records };
    deleteSummary(params);
  };

  const {
    loading: newTagLoading,
    noNewTagsReport,
    setReportView,
  } = useAiReportNewTag({
    data: records,
    needRequest: !isOneTime,
  });

  const downloadSummaryMap = useDownloadSummaryItem();
  const previewSummaryMap = usePreviewSummaryItem();

  const handlePermissionSaved = useCallback(() => {
    requestSummaryList(params);
  }, [params, requestSummaryList]);

  const { open: openPermissionPanel, openByTarget, visible, target, close } =
    useOpenReportPermission();

  const { columns, sortColumns } = useSummaryListColumns({
    clickDelete,
    clickPreview: (records: any) => {
      if (!isOneTime && !noNewTagsReport[records.id]) {
        setReportView(String(records.id));
      }
      const key = isOneTime ? 'common' : 'repeat_summary_task';
      previewSummaryMap[key]?.({ ...records, gameInfo }, undefined, owner);
    },
    clickDownload: (records: any) => {
      const key = isOneTime ? 'common' : 'repeat_summary_task';
      downloadSummaryMap[key]?.({ ...records, gameInfo });
    },
    keys,
    toggleModal,
    noNewTagsReport: isOneTime ? undefined : noNewTagsReport,
    newTagLoading: isOneTime ? undefined : newTagLoading,
    onOpenPermissionPanel: openPermissionPanel,
    clickStop: (records: any) => {
      buriedBtnPoint('Stop', 'Subscriptions Summary');
      stopSummary({ ...records, gameInfo });
    }
  });

  return (
    <>
      <TableCommonHook
        columns={columns}
        sortColumns={sortColumns}
        records={records}
        total={total}
        loading={loading}
        pageInfo={pageInfo}
        setPageInfo={changePageInfo}
        sortInfo={[sortBy, setSortBy]}
      ></TableCommonHook>
      <ReportAccessPanel
        visible={visible}
        target={target}
        onClose={close}
        onSaved={handlePermissionSaved}
        notificationMode="list"
        onPermissionLinkClick={openByTarget}
      />
    </>
  );
}
